/**
 * Tile-scale album art — local disk thumb first, remote cover_art_url fallback.
 * v11.0.215+ serves pre-generated JPEGs at GET /thumb/{16-char-key}.
 *
 * Session cache stores plain endpoint URLs (never blob:) so navigation retention
 * does not serve revoked object URLs after tile unmount.
 */

import {
  isBadCoverUrl,
  pickCoverArtThumbHash,
  validateThumbBlob,
  withCoverThumbParam,
} from './coverArt';
import { buildLocalThumbUrl, isHttpsCoverContext } from '../lib/connector';

export { MIN_VALID_THUMB_BYTES, validateThumbBlob, validateThumbArrayBuffer } from './coverArt';

/** Session-scoped resolved tile art — memory only, never persisted. */
export const TILE_COVER_SESSION_CACHE_MAX = 600;

const tileCoverSessionLru = new Map<string, string>();

let _thumbEndpointAvailable: boolean | null = null;
let _thumbEndpointProbe: Promise<boolean> | null = null;

let _thumbHit = 0;
let _thumbMissFallback = 0;
let _thumbStubRejected = 0;
let _cacheHit = 0;
let _tileErrorCycles = 0;

/** Session keys that already failed img onError — max one invalidation per key. */
const tileCoverExhaustedKeys = new Set<string>();

export type TileCoverArtStats = {
  thumb_hit: number;
  thumb_miss_fallback: number;
  thumb_stub_rejected: number;
  thumb_endpoint_available: boolean | null;
  cache_hit: number;
  /** Must stay 0 — tile cache never stores blob: URLs. */
  tile_cache_blob_urls: number;
  /** resolve → invalidate → re-resolve repetitions blocked by cycle guard. */
  tile_error_cycles: number;
};

function countTileCacheBlobUrls(): number {
  let n = 0;
  for (const url of tileCoverSessionLru.values()) {
    if (url.startsWith('blob:')) n += 1;
  }
  return n;
}

export function getTileCoverArtStats(): TileCoverArtStats {
  return {
    thumb_hit: _thumbHit,
    thumb_miss_fallback: _thumbMissFallback,
    thumb_stub_rejected: _thumbStubRejected,
    thumb_endpoint_available: _thumbEndpointAvailable,
    cache_hit: _cacheHit,
    tile_cache_blob_urls: countTileCacheBlobUrls(),
    tile_error_cycles: _tileErrorCycles,
  };
}

function evictOldestTileCoverSessionEntry(): void {
  const oldest = tileCoverSessionLru.keys().next().value as string | undefined;
  if (!oldest) return;
  tileCoverSessionLru.delete(oldest);
}

function setTileCoverSessionCache(key: string, url: string): void {
  if (url.startsWith('blob:')) {
    // Defensive — callers must cache endpoint or remote URLs only.
    return;
  }
  tileCoverSessionLru.delete(key);
  tileCoverSessionLru.set(key, url);
  while (tileCoverSessionLru.size > TILE_COVER_SESSION_CACHE_MAX) {
    evictOldestTileCoverSessionEntry();
  }
}

function getTileCoverSessionCache(key: string): string | null {
  const url = tileCoverSessionLru.get(key);
  if (!url) return null;
  tileCoverSessionLru.delete(key);
  tileCoverSessionLru.set(key, url);
  _cacheHit += 1;
  return url;
}

function invalidateTileCoverSessionCache(key: string): void {
  tileCoverSessionLru.delete(key);
}

/** Stable session cache key from album identity + art sources. */
export function buildTileCoverAlbumCacheKey(
  album: Record<string, unknown> | null | undefined,
): string | null {
  if (!album) return null;
  const id = String(
    album.album_key || album.albumKey || album.id || album.album_id || '',
  ).trim();
  if (!id) return null;
  const thumb = pickCoverArtThumbHash(album) || '';
  const remote = pickRemoteTileCoverUrl(album) || '';
  return `album:${id}|${thumb}|${remote}`;
}

function buildTileCoverThumbCacheKey(thumbKey: string): string {
  return `thumb:${thumbKey}`;
}

function isTileCoverKeyExhausted(...keys: (string | null | undefined)[]): boolean {
  return keys.some((k) => k && tileCoverExhaustedKeys.has(k));
}

/** Returns true when this is a repeat invalidation (cycle). */
function markTileCoverExhausted(...keys: (string | null | undefined)[]): boolean {
  const primary = keys.find((k) => k && String(k).trim());
  if (!primary) return false;
  if (tileCoverExhaustedKeys.has(primary)) {
    _tileErrorCycles += 1;
    return true;
  }
  for (const k of keys) {
    if (k) tileCoverExhaustedKeys.add(k);
  }
  return false;
}

function cacheRemoteFallbackForAlbum(
  album: Record<string, unknown> | null | undefined,
): string | null {
  const remote = pickRemoteTileCoverUrl(album);
  const albumKey = buildTileCoverAlbumCacheKey(album);
  if (remote && albumKey) setTileCoverSessionCache(albumKey, remote);
  return remote;
}

export function invalidateTileCoverArtForAlbum(
  album: Record<string, unknown> | null | undefined,
): void {
  const albumKey = buildTileCoverAlbumCacheKey(album);
  if (albumKey) invalidateTileCoverSessionCache(albumKey);
  const thumb = pickCoverArtThumbHash(album);
  if (thumb) invalidateTileCoverSessionCache(buildTileCoverThumbCacheKey(thumb));
}

/** Call from img onError — once per key; then remote-and-stay. */
export function invalidateTileCoverArtOnLoadError(
  album: Record<string, unknown> | null | undefined,
): void {
  const albumKey = buildTileCoverAlbumCacheKey(album);
  const thumb = pickCoverArtThumbHash(album);
  const thumbCacheKey = thumb ? buildTileCoverThumbCacheKey(thumb) : null;
  if (markTileCoverExhausted(albumKey, thumbCacheKey)) return;
  invalidateTileCoverArtForAlbum(album);
  cacheRemoteFallbackForAlbum(album);
}

export function resetTileCoverArtStatsForTests(): void {
  _thumbHit = 0;
  _thumbMissFallback = 0;
  _thumbStubRejected = 0;
  _cacheHit = 0;
  _tileErrorCycles = 0;
  _thumbEndpointAvailable = null;
  _thumbEndpointProbe = null;
  tileCoverExhaustedKeys.clear();
}

export function resetTileCoverSessionCacheForTests(): void {
  tileCoverSessionLru.clear();
  artistCollageSessionLru.clear();
  tileCoverExhaustedKeys.clear();
  _tileErrorCycles = 0;
}

export function setTileCoverSessionCacheForTests(key: string, url: string): void {
  setTileCoverSessionCache(key, url);
}

export function getTileCoverSessionCacheSizeForTests(): number {
  return tileCoverSessionLru.size;
}

export function getTileCoverSessionCacheUrlsForTests(): string[] {
  return Array.from(tileCoverSessionLru.values());
}

function recordThumbHit(): void {
  _thumbHit += 1;
}

function recordThumbMissFallback(): void {
  _thumbMissFallback += 1;
}

function recordThumbStubRejected(): void {
  _thumbStubRejected += 1;
}

function parseConnectorVersionParts(): number[] | null {
  if (typeof window === 'undefined') return null;
  const raw = (window as Window & { __MAESTRO_CONNECTOR__?: { version?: string } }).__MAESTRO_CONNECTOR__?.version;
  if (!raw) return null;
  const parts = String(raw).replace(/^v/i, '').split('.').map((p) => Number(p));
  if (parts.some((n) => !Number.isFinite(n))) return null;
  return parts;
}

/** semver >= 11.0.215 when connector version is advertised. */
export function connectorVersionSupportsDiskThumbs(): boolean | null {
  const parts = parseConnectorVersionParts();
  if (!parts || parts.length < 3) return null;
  const [major, minor, patch] = parts;
  if (major > 11) return true;
  if (major < 11) return false;
  if (minor > 0) return true;
  if (minor < 0) return false;
  return patch >= 215;
}

/**
 * One probe per session — 404 from /thumb/ means the route exists (v11.0.215+).
 * Connectors without the route or without a proxy port → remote-only for the session.
 */
export async function probeLocalThumbEndpoint(): Promise<boolean> {
  if (_thumbEndpointAvailable !== null) return _thumbEndpointAvailable;
  if (_thumbEndpointProbe) return _thumbEndpointProbe;

  _thumbEndpointProbe = (async () => {
    const versionGate = connectorVersionSupportsDiskThumbs();
    if (versionGate === false) {
      _thumbEndpointAvailable = false;
      return false;
    }
    if (isHttpsCoverContext()) {
      _thumbEndpointAvailable = versionGate !== false;
      return _thumbEndpointAvailable;
    }

    const syncUrl = buildLocalThumbUrl('0000000000000000');
    if (!syncUrl) {
      _thumbEndpointAvailable = false;
      return false;
    }

    try {
      const resp = await fetch(syncUrl, { method: 'GET', cache: 'no-store' });
      _thumbEndpointAvailable = resp.status === 404 || resp.ok;
      return _thumbEndpointAvailable;
    } catch {
      _thumbEndpointAvailable = false;
      return false;
    } finally {
      _thumbEndpointProbe = null;
    }
  })();

  return _thumbEndpointProbe;
}

export function isLocalThumbEndpointAvailable(): boolean | null {
  return _thumbEndpointAvailable;
}

/** Normalize remote tile fallback from album record fields. */
export function pickRemoteTileCoverUrl(
  album: Record<string, unknown> | null | undefined,
): string | null {
  if (!album) return null;
  const candidates = [
    album.cover_art_url,
    album.coverUrl,
    album.cover_url,
    album.image_url,
    album.artworkUrl,
  ];
  for (const raw of candidates) {
    if (!raw || isBadCoverUrl(String(raw))) continue;
    let normalized = String(raw).trim();
    if (normalized.startsWith('http://')) {
      normalized = normalized.replace(/^http:\/\//i, 'https://');
    }
    return withCoverThumbParam(normalized) || normalized;
  }
  return null;
}

export function albumHasLocalThumbSource(
  album: Record<string, unknown> | null | undefined,
): boolean {
  if (!album) return false;
  if (pickCoverArtThumbHash(album)) return true;
  const emb = String(album.embedded_cover_hash || album.embeddedCoverHash || '').trim();
  const fol = String(album.folder_cover_hash || album.folderCoverHash || '').trim();
  const ext = String(album.external_cover_hash || album.externalCoverHash || '').trim();
  return emb.length > 0 || fol.length > 0 || ext.length > 0;
}

/**
 * First-fetch validation for a disk thumb — caches plain GET /thumb/{key} URL on success.
 */
export async function fetchValidatedLocalThumbUrl(
  thumbKey: string | null | undefined,
): Promise<string | null> {
  const key = pickCoverArtThumbHash({ cover_art_thumb_hash: thumbKey });
  if (!key) return null;

  const thumbCacheKey = buildTileCoverThumbCacheKey(key);
  const cached = getTileCoverSessionCache(thumbCacheKey);
  if (cached) return cached;

  const endpointOk = await probeLocalThumbEndpoint();
  if (!endpointOk) return null;

  if (isHttpsCoverContext()) {
    const { getCoverArtByCacheKey, isCoverIPCAvailable } = await import('../lib/coverArtIPC');
    if (!isCoverIPCAvailable()) return null;
    const dataUrl = await getCoverArtByCacheKey(key, {});
    if (!dataUrl) {
      invalidateTileCoverSessionCache(thumbCacheKey);
      recordThumbMissFallback();
      return null;
    }
    try {
      const resp = await fetch(dataUrl);
      const blob = await resp.blob();
      if (!validateThumbBlob(blob)) {
        invalidateTileCoverSessionCache(thumbCacheKey);
        recordThumbStubRejected();
        recordThumbMissFallback();
        return null;
      }
      recordThumbHit();
      setTileCoverSessionCache(thumbCacheKey, dataUrl);
      return dataUrl;
    } catch {
      invalidateTileCoverSessionCache(thumbCacheKey);
      recordThumbMissFallback();
      return null;
    }
  }

  const httpUrl = buildLocalThumbUrl(key);
  if (!httpUrl) {
    invalidateTileCoverSessionCache(thumbCacheKey);
    recordThumbMissFallback();
    return null;
  }
  try {
    const resp = await fetch(httpUrl, { cache: 'no-store' });
    if (!resp.ok) {
      invalidateTileCoverSessionCache(thumbCacheKey);
      recordThumbMissFallback();
      return null;
    }
    const blob = await resp.blob();
    if (!validateThumbBlob(blob)) {
      invalidateTileCoverSessionCache(thumbCacheKey);
      recordThumbStubRejected();
      recordThumbMissFallback();
      return null;
    }
    recordThumbHit();
    setTileCoverSessionCache(thumbCacheKey, httpUrl);
    return httpUrl;
  } catch {
    invalidateTileCoverSessionCache(thumbCacheKey);
    recordThumbMissFallback();
    return null;
  }
}

/** Sync first paint URL — session cache, then local /thumb/ when probe already succeeded. */
export function syncLocalThumbDisplayUrl(
  album: Record<string, unknown> | null | undefined,
): string | null {
  const albumCacheKey = buildTileCoverAlbumCacheKey(album);
  if (albumCacheKey) {
    const cachedAlbum = getTileCoverSessionCache(albumCacheKey);
    if (cachedAlbum) return cachedAlbum;
  }

  const key = pickCoverArtThumbHash(album);
  if (key) {
    const cachedThumb = getTileCoverSessionCache(buildTileCoverThumbCacheKey(key));
    if (cachedThumb) return cachedThumb;
  }

  if (isTileCoverKeyExhausted(albumCacheKey, key ? buildTileCoverThumbCacheKey(key) : null)) {
    return cacheRemoteFallbackForAlbum(album);
  }

  if (!key) return null;
  if (_thumbEndpointAvailable === false) return null;
  return buildLocalThumbUrl(key);
}

/**
 * Tile-scale resolution order:
 * (a) validated local disk thumb (endpoint URL)
 * (b) remote cover_art_url
 * (c) null → caller shows placeholder
 */
export async function resolveTileCoverDisplayUrl(
  album: Record<string, unknown> | null | undefined,
): Promise<string | null> {
  const albumCacheKey = buildTileCoverAlbumCacheKey(album);
  if (albumCacheKey) {
    const cached = getTileCoverSessionCache(albumCacheKey);
    if (cached) return cached;
  }

  const thumbKey = pickCoverArtThumbHash(album);
  if (thumbKey) {
    const cachedThumb = getTileCoverSessionCache(buildTileCoverThumbCacheKey(thumbKey));
    if (cachedThumb) {
      if (albumCacheKey) setTileCoverSessionCache(albumCacheKey, cachedThumb);
      return cachedThumb;
    }
  }

  if (thumbKey) {
    const thumbCacheKey = buildTileCoverThumbCacheKey(thumbKey);
    if (isTileCoverKeyExhausted(albumCacheKey, thumbCacheKey)) {
      return cacheRemoteFallbackForAlbum(album);
    }
    const local = await fetchValidatedLocalThumbUrl(thumbKey);
    if (local) {
      if (albumCacheKey) setTileCoverSessionCache(albumCacheKey, local);
      return local;
    }
  }

  const remote = pickRemoteTileCoverUrl(album);
  if (remote) {
    if (thumbKey) recordThumbMissFallback();
    if (albumCacheKey) setTileCoverSessionCache(albumCacheKey, remote);
    return remote;
  }

  if (thumbKey) recordThumbMissFallback();
  if (albumCacheKey) invalidateTileCoverSessionCache(albumCacheKey);
  return null;
}

/** Artist browse tile collage — session cache of immortal endpoint/remote URLs. */
const artistCollageSessionLru = new Map<string, string[]>();

export function buildArtistTileCacheKey(
  artist: Record<string, unknown> | null | undefined,
): string | null {
  if (!artist) return null;
  const name = String(artist.name || '').trim();
  if (!name) return null;
  const portrait = String(artist.image_url || '').trim();
  const samples = (artist.sampleAlbums as Record<string, unknown>[] | undefined)
    || (artist.albums as Record<string, unknown>[] | undefined)
    || [];
  const sampleSig = samples
    .slice(0, 4)
    .map((a) => [
      a?.album_key || a?.id || '',
      pickCoverArtThumbHash(a) || '',
      a?.embedded_cover_hash || '',
      a?.folder_cover_hash || '',
      a?.cover_art_url || '',
    ].join(':'))
    .join(';');
  return `artist:${name}|${portrait}|${sampleSig}`;
}

function getArtistCollageSessionCache(key: string): string[] | null {
  const urls = artistCollageSessionLru.get(key);
  if (!urls?.length) return null;
  artistCollageSessionLru.delete(key);
  artistCollageSessionLru.set(key, urls);
  return urls.slice();
}

function setArtistCollageSessionCache(key: string, urls: string[]): void {
  if (!urls.length || urls.some((u) => u.startsWith('blob:'))) return;
  artistCollageSessionLru.delete(key);
  artistCollageSessionLru.set(key, urls.slice());
  while (artistCollageSessionLru.size > TILE_COVER_SESSION_CACHE_MAX) {
    const oldest = artistCollageSessionLru.keys().next().value as string | undefined;
    if (!oldest) break;
    artistCollageSessionLru.delete(oldest);
  }
}

/** Sync-first collage URLs for artist tiles (endpoint cache + remote). */
export function syncArtistCollageUrls(
  artist: Record<string, unknown> | null | undefined,
  max = 4,
): string[] {
  if (!artist) return [];
  if (artist.image_url && !isBadCoverUrl(String(artist.image_url))) {
    return [String(artist.image_url)];
  }
  const cacheKey = buildArtistTileCacheKey(artist);
  if (cacheKey) {
    const cached = getArtistCollageSessionCache(cacheKey);
    if (cached?.length) return cached;
  }
  if (cacheKey && isTileCoverKeyExhausted(cacheKey)) {
    return [];
  }
  const albums = (
    (artist.sampleAlbums as Record<string, unknown>[] | undefined)
    || (artist.albums as Record<string, unknown>[] | undefined)
    || []
  ).slice(0, max);
  const urls: string[] = [];
  for (const album of albums) {
    const u = syncLocalThumbDisplayUrl(album) || pickRemoteTileCoverUrl(album);
    if (u && !urls.includes(u)) urls.push(u);
  }
  if (cacheKey && urls.length) setArtistCollageSessionCache(cacheKey, urls);
  return urls;
}

/** Async resolve for artist collage when sync path misses — viewport queue + cache. */
export async function resolveArtistCollageDisplayUrls(
  artist: Record<string, unknown> | null | undefined,
  max = 4,
): Promise<string[]> {
  const sync = syncArtistCollageUrls(artist, max);
  if (sync.length > 0) return sync;

  const cacheKey = buildArtistTileCacheKey(artist);
  if (cacheKey) {
    const cached = getArtistCollageSessionCache(cacheKey);
    if (cached?.length) return cached;
  }
  if (cacheKey && isTileCoverKeyExhausted(cacheKey)) return [];

  const albums = (
    (artist?.sampleAlbums as Record<string, unknown>[] | undefined)
    || (artist?.albums as Record<string, unknown>[] | undefined)
    || []
  ).slice(0, max);
  const { resolveCollageCovers } = await import('../lib/viewportCoverQueue');
  const urls = (await resolveCollageCovers(albums, max)).filter(Boolean) as string[];
  if (cacheKey && urls.length) setArtistCollageSessionCache(cacheKey, urls);
  return urls;
}

/** img onError — once per artist key; sample albums fall back to remote-and-stay. */
export function invalidateArtistTileOnLoadError(
  artist: Record<string, unknown> | null | undefined,
): void {
  const cacheKey = buildArtistTileCacheKey(artist);
  if (markTileCoverExhausted(cacheKey)) return;
  if (cacheKey) artistCollageSessionLru.delete(cacheKey);
  const albums = (
    (artist?.sampleAlbums as Record<string, unknown>[] | undefined)
    || (artist?.albums as Record<string, unknown>[] | undefined)
    || []
  );
  const fallbackUrls: string[] = [];
  for (const album of albums) {
    invalidateTileCoverArtForAlbum(album);
    const remote = cacheRemoteFallbackForAlbum(album);
    if (remote && !fallbackUrls.includes(remote)) fallbackUrls.push(remote);
  }
  if (cacheKey && fallbackUrls.length) setArtistCollageSessionCache(cacheKey, fallbackUrls);
}

export function resetArtistCollageSessionCacheForTests(): void {
  artistCollageSessionLru.clear();
}

export function getArtistCollageSessionCacheSizeForTests(): number {
  return artistCollageSessionLru.size;
}

export function getTileErrorCyclesForTests(): number {
  return _tileErrorCycles;
}
